// Página principal responsiva de Icarolife con estructura clara de header, body y footer
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import translations from '../translations/HomePage.translations.js';
import FeaturesGrid from "./FeaturesGrid.jsx";

export default function HomePage() {
  const getDefaultLang = () => {
    const lang = navigator.language || navigator.userLanguage;
    if (lang && translations[lang]) return lang;
    if (lang && lang.startsWith("es")) return "es";
    if (lang && lang.startsWith("en")) return "en";
    return "en";
  };

  const [lang, setLang] = React.useState("es");
  const [darkMode, setDarkMode] = React.useState(false);
  const t = translations[lang] || translations['en'];
  const navigate = useNavigate();

  // Estados para mostrar/ocultar features
  const [showPacienteFeatures, setShowPacienteFeatures] = useState(false);
  const [showDoctorFeatures, setShowDoctorFeatures] = useState(false);

  // Features de cada grupo
  const funcionesPacientes = [
    t.gestionCitas,
    t.historial,
    t.consultas,
    "Almacenamiento de Exámenes Médicos",
    "Acceso a Doctores Certificados",
    "Consultas Médicas por Videollamada o Chat",
    "Atención Médica Continua",
    "Seguridad y Privacidad",
    "Soporte por WhatsApp"
  ];
  const funcionesDoctores = [
    t.panel,
    t.remotas,
    t.gestionPagos,
    "Certificación Médica en Línea",
    "Ganancias Recurrentes",
    "Recordatorios Automáticos para Pacientes",
    "Historial Médico Compartido",
    "Agenda Inteligente",
    "Presencia Internacional",
    "Soporte Administrativo Integrado",
    "Reputación y Ranking Profesional"
  ];

  React.useEffect(() => {
    if (darkMode) {
      document.body.classList.add("dark-theme");
    } else {
      document.body.classList.remove("dark-theme");
    }
  }, [darkMode]);

  const handleDoctorLogin = () => {
    navigate("/login-doctor");
  };

  const handlePacienteLogin = () => {
    navigate("/login-paciente");
  };

  // Selecciona la imagen según el idioma
  const bannerImg =
    lang === "en"
      ? "/images/icarolife-banner-en.png"
      : "/images/icarolife-banner-es.png";

  if (!t) return <div>Error de traducción: idioma no soportado</div>;

  return (
    <div className="homepage-container">
      <section className="homepage-caja-row homepage-caja-header">
        {/* Header y banner aquí */}
        <header className="header-flex">
          <div className="header-left">
            <img
              src="/images/icarolife-logo.png"
              alt="IcaroLife Logo"
              className="logo-principal"
            />
            <h1 className="header-title">IcaroLife</h1>
          </div>
          <div className="header-actions">
            <button
              className="header-btn"
              onClick={() => setDarkMode((d) => !d)}
              aria-label="Cambiar tema"
              title={darkMode ? "Modo claro" : "Modo oscuro"}
            >
              {darkMode ? "🌞" : "🌙"}
            </button>
            <button
              className="header-btn"
              onClick={() => setLang((l) => (l === "es" ? "en" : "es"))}
              aria-label="Cambiar idioma"
              title={lang === "es" ? "Switch to English" : "Cambiar a Español"}
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
          </div>
        </header>
        <hr className="divider" />
        <section className="banner-card-full">
          <img
            src={bannerImg}
            alt="Slogan IcaroLife"
            className="banner-img-full"
          />
          <p className="slogan-banner">{t.slogan}</p>
        </section>
      </section>

      <div className="homepage-cajas-row">
        {/* Pacientes */}
        <section className="homepage-caja-individual">
          <img
            src="/images/icarolife-pacient.png"
            alt={t.pacientes}
            className="logo-paciente"
            style={{ width: "64px", height: "64px", marginBottom: "1em" }}
          />
          <h2>{t.pacientes}</h2>
          <button
            className="btn btn-outline btn-login btn-paciente"
            onClick={() => setShowPacienteFeatures((v) => !v)}
          >
            {showPacienteFeatures ? t.ocultarFeatures || "Ocultar características" : t.verFeatures || "Ver todas las características"}
          </button>
          {showPacienteFeatures && (
            <ul className="features-list-pacientes show-all">
              {funcionesPacientes.map((f, i) => (
                <li key={i}>
                  <i className="fas fa-user-injured feature-icon"></i> {f}
                </li>
              ))}
            </ul>
          )}
          <div className="feature-actions">
            <a
              className="btn btn-whatsapp btn-login"
              href="https://wa.me/584142269042"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-whatsapp btn-icon"></i>
              {t.chatear}
            </a>
            <a
              className="btn btn-playstore btn-login"
              href="https://play.google.com/store/apps/details?id=icarolife"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-google-play btn-icon"></i>
              {t.descargarPaciente}
            </a>
            <button
              className="btn btn-dashboard btn-login"
              onClick={handlePacienteLogin}
            >
              <i className="fas fa-user-circle btn-icon"></i>
              <span className="btn-dashboard-text">{t.accesoPacientes}</span>
            </button>
          </div>
        </section>
        {/* Doctores */}
        <section className="homepage-caja-individual">
          <img
            src="/images/icarolife-doctor.png"
            alt={t.doctores}
            className="logo-doctor"
            style={{ width: "64px", height: "64px", marginBottom: "1em" }}
          />
          <h2>{t.doctores}</h2>
          <button
            className="btn btn-outline btn-login btn-doctor"
            onClick={() => setShowDoctorFeatures((v) => !v)}
          >
            {showDoctorFeatures ? t.ocultarFeatures || "Ocultar características" : t.verFeatures || "Ver todas las características"}
          </button>
          {showDoctorFeatures && (
            <ul className="features-list-doctores show-all">
              {funcionesDoctores.map((f, i) => (
                <li key={i}>
                  <i className="fas fa-user-md feature-icon"></i> {f}
                </li>
              ))}
            </ul>
          )}
          <div className="feature-actions">
            <a
              className="btn btn-whatsapp btn-login"
              href="https://wa.me/584142265320"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-whatsapp btn-icon"></i>
              {t.chatear}
            </a>
            <a
              className="btn btn-playstore btn-doctor-play btn-login"
              href="https://play.google.com/store/apps/details?id=icarolife.doctor"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-google-play btn-icon"></i>
              {t.descargarDoctor}
            </a>
            <button
              className="btn btn-dashboard btn-login"
              onClick={handleDoctorLogin}
            >
              <i className="fas fa-user-md btn-icon"></i>
              <span className="btn-dashboard-text">{t.accesoDoctores}</span>
            </button>
          </div>
        </section>
      </div>

      {/* Footer en caja */}
      <section className="homepage-caja-row homepage-caja-footer">
        <footer>
          <p>
            <a href={`mailto:${t.contacto}`} className="footer-link">
              <i className="fas fa-envelope footer-icon"></i> {t.contacto}
            </a>
          </p>
          <a
            href="https://www.facebook.com/648087325057261"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-link"
          >
            <i className="fab fa-facebook footer-icon"></i> {t.facebook}
          </a>
          <a
            href="https://instagram.com/icarolife.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-link"
          >
            <i className="fab fa-instagram footer-icon"></i> Instagram
          </a>
          <p>{t.derechos}</p>
        </footer>
      </section>
    </div>
  );
}